import React, { useState } from "react";
import { Box, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, useMediaQuery } from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  EditOutlined as EditIcon,
  SendOutlined as SubmitIcon,
  UndoOutlined as WithdrawIcon,
  CheckCircleOutline as ConfirmIcon,
  PlayArrowRounded as PlayIcon,
  TaskAltOutlined as CompleteIcon,
  HourglassBottomRounded as WaitingIcon,
  ThumbUpOutlined as AcceptIcon,
  ThumbDownOutlined as RejectIcon,
  CompareArrowsOutlined as CompareIcon,
  CancelOutlined as CancelIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { dt } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { useConfirmation } from "../../../../shared/components/confirmation/ConfirmationProvider";
import {
  withdrawOffer,
  deleteOffer,
  submitOffer,
  startWork,
  getOffer,
  getOffers,
} from "../../customerOffersSlice";
import { Offer, OfferStatusCode, OffersState } from "../../models/models";
import receivedOffersApi from "../../../servicing/api/receivedOffersApi";

interface OfferActionBarProps {
  offer: Offer;
  isOwner?: boolean;
  serviceRequestId?: string;
  onConfirmServiceOpen: () => void;
  onDataRefresh: () => void;
}

const OfferActionBar: React.FC<OfferActionBarProps> = ({
  offer,
  isOwner = false,
  serviceRequestId,
  onConfirmServiceOpen,
  onDataRefresh,
}) => {
  const { translate } = useTranslation(["serviceOffers", "servicing", "common"]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const confirm = useConfirmation();
  const isPhone = useMediaQuery("(max-width:768px)");
  const isTablet = useMediaQuery("(min-width:769px) and (max-width:1200px)");

  const { loading } = useAppSelector<OffersState>((state: any) => state.customerOffers);

  // Reject reason dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const statusCode = offer.statusCode;

  // ═════════════════════════════════════
  //  Provider action flags
  // ═════════════════════════════════════
  const canEdit = !isOwner && statusCode === OfferStatusCode.draft;
  const canSubmit = !isOwner && statusCode === OfferStatusCode.draft;
  const canWithdraw = !isOwner && [OfferStatusCode.sent, OfferStatusCode.viewed, OfferStatusCode.negotiating].includes(statusCode);
  const canConfirmProvider = !isOwner && statusCode === OfferStatusCode.awaitingConfirmation;
  const canStartWork = !isOwner && statusCode === OfferStatusCode.confirmed;
  const isWorkInProgressProvider = !isOwner && statusCode === OfferStatusCode.workInProgress;
  const isWorkCompletedProvider = !isOwner && statusCode === OfferStatusCode.workCompleted;
  const isServiceCompletedProvider = !isOwner && statusCode === OfferStatusCode.serviceCompleted;

  // ═════════════════════════════════════
  //  Owner action flags
  // ═════════════════════════════════════
  const canAccept = isOwner && [OfferStatusCode.sent, OfferStatusCode.viewed, OfferStatusCode.negotiating].includes(statusCode);
  const canReject = isOwner && [OfferStatusCode.sent, OfferStatusCode.viewed, OfferStatusCode.negotiating].includes(statusCode);
  const canCancelAcceptance = isOwner && statusCode === OfferStatusCode.awaitingConfirmation;
  const isWaitingForProvider = isOwner && [OfferStatusCode.awaitingConfirmation, OfferStatusCode.confirmed].includes(statusCode);
  const isOwnerWorkInProgress = isOwner && statusCode === OfferStatusCode.workInProgress;
  const isOwnerWorkCompleted = isOwner && statusCode === OfferStatusCode.workCompleted;
  const isOwnerServiceCompleted = isOwner && statusCode === OfferStatusCode.serviceCompleted;

  const isTerminal = [OfferStatusCode.rejected, OfferStatusCode.expired, OfferStatusCode.withdrawn].includes(statusCode);

  // ═════════════════════════════════════
  //  Handlers
  // ═════════════════════════════════════
  const handleBack = () => {
    if (isOwner && serviceRequestId) {
      navigate(`/service-board/${serviceRequestId}`);
    } else if (isOwner) {
      navigate("/service-requests");
    } else {
      navigate("/service-offers");
    }
  };

  const handleEdit = () => navigate(`/service-offers/edit/${offer.id}`);

  const handleSubmitOffer = async () => {
    await dispatch(submitOffer(offer.id));
    onDataRefresh();
  };

  const handleWithdraw = () => {
    confirm({
      title: translate("withdrawOfferTitle"),
      text: translate("withdrawOfferText", { number: offer.internalNumber }),
      confirmButton: translate("yesWithdraw"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: async () => {
        await dispatch(withdrawOffer(offer.id));
        onDataRefresh();
      },
    });
  };

  const handleStartWork = () => {
    confirm({
      title: translate("serviceOffers:execution.startWorkTitle"),
      text: translate("serviceOffers:execution.startWorkText", { number: offer.internalNumber }),
      confirmButton: translate("serviceOffers:execution.yesStartWork"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: async () => {
        await dispatch(startWork(offer.id));
        onDataRefresh();
      },
    });
  };

  // Owner: Accept offer
  const handleAcceptOffer = () => {
    if (!serviceRequestId) return;
    confirm({
      title: translate("servicing:acceptOfferTitle"),
      text: translate("servicing:acceptOfferText"),
      confirmButton: translate("servicing:acceptOffer"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await receivedOffersApi.acceptOffer(serviceRequestId, offer.id);
          onDataRefresh();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  // Owner: Reject offer (with reason dialog)
  const handleRejectSubmit = async () => {
    if (!serviceRequestId) return;
    setActionLoading(true);
    try {
      await receivedOffersApi.rejectOffer(serviceRequestId, offer.id, rejectReason || undefined);
      setRejectDialogOpen(false);
      setRejectReason("");
      onDataRefresh();
    } finally {
      setActionLoading(false);
    }
  };

  // Owner: Cancel acceptance
  const handleCancelAcceptance = () => {
    if (!serviceRequestId) return;
    confirm({
      title: translate("servicing:cancelAcceptanceTitle"),
      text: translate("servicing:cancelAcceptanceText"),
      confirmButton: translate("servicing:cancelAcceptance"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: async () => {
        setActionLoading(true);
        try {
          await receivedOffersApi.cancelAcceptance(serviceRequestId, offer.id);
          onDataRefresh();
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  // Owner: Compare offers
  const handleCompareOffers = () => {
    if (serviceRequestId) {
      navigate(`/service-board/${serviceRequestId}/offers/compare`);
    }
  };

  const isFixed = isPhone || isTablet;

  const barSx = isFixed
    ? {
        position: "fixed" as const,
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1100,
        backgroundColor: "#FFFFFF",
        borderTop: `1px solid ${dt.cardBorder}`,
        boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
        padding: "12px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        flexWrap: "wrap" as const,
      }
    : {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1.5,
        flexWrap: "wrap" as const,
        padding: "16px 0",
      };

  const btnBase = {
    textTransform: "none" as const,
    fontFamily: dt.font,
    fontWeight: 600,
    fontSize: "13px",
    borderRadius: dt.radiusSm,
  };

  return (
    <>
      <Box sx={barSx}>
        {/* Back button — always present */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{
            ...btnBase,
            color: dt.textSecondary,
            "&:hover": { backgroundColor: dt.blueSoft, color: dt.blue },
          }}
        >
          {isOwner
            ? translate("serviceOffers:offerDetail.backToRequest")
            : translate("backToOffers")}
        </Button>

        {/* ═══════════════════════════════
             PROVIDER ACTIONS
           ═══════════════════════════════ */}

        {/* Draft: Edit + Submit */}
        {canEdit && (
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            sx={{
              ...btnBase,
              borderColor: dt.cardBorder,
              color: dt.textPrimary,
              "&:hover": { borderColor: dt.blue, color: dt.blue },
            }}
          >
            {translate("editOffer")}
          </Button>
        )}
        {canSubmit && (
          <Button
            variant="contained"
            startIcon={<SubmitIcon />}
            onClick={handleSubmitOffer}
            disabled={loading}
            sx={{
              ...btnBase,
              backgroundColor: dt.blue,
              "&:hover": { backgroundColor: dt.blueHover },
            }}
          >
            {translate("submitOffer")}
          </Button>
        )}

        {/* Sent/Viewed/Negotiating: Withdraw */}
        {canWithdraw && (
          <Button
            variant="outlined"
            startIcon={<WithdrawIcon />}
            onClick={handleWithdraw}
            sx={{
              ...btnBase,
              borderColor: "#F59E0B",
              color: "#F59E0B",
              "&:hover": { borderColor: "#D97706", color: "#D97706", backgroundColor: "rgba(245,158,11,0.04)" },
            }}
          >
            {translate("withdraw")}
          </Button>
        )}

        {/* Awaiting Confirmation: Confirm Service (provider) */}
        {canConfirmProvider && (
          <Button
            variant="contained"
            startIcon={<ConfirmIcon />}
            onClick={onConfirmServiceOpen}
            sx={{
              ...btnBase,
              fontWeight: 700,
              fontSize: "14px",
              backgroundColor: "#F59E0B",
              color: "#FFFFFF",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(245,158,11,0.4)" },
                "70%": { boxShadow: "0 0 0 10px rgba(245,158,11,0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(245,158,11,0)" },
              },
              "&:hover": { backgroundColor: "#D97706" },
            }}
          >
            {translate("confirmService")}
          </Button>
        )}

        {/* Confirmed: Start Work (provider) */}
        {canStartWork && (
          <Button
            variant="contained"
            startIcon={<PlayIcon />}
            onClick={handleStartWork}
            disabled={loading}
            sx={{
              ...btnBase,
              fontWeight: 700,
              fontSize: "14px",
              backgroundColor: "#14B8A6",
              color: "#FFFFFF",
              animation: "pulse 2s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(20,184,166,0.4)" },
                "70%": { boxShadow: "0 0 0 10px rgba(20,184,166,0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(20,184,166,0)" },
              },
              "&:hover": { backgroundColor: "#0D9488" },
            }}
          >
            {translate("execution.startWork")}
          </Button>
        )}

        {/* Work In Progress hint (provider) */}
        {isWorkInProgressProvider && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#3B82F6",
              fontFamily: dt.font,
              fontWeight: 600,
              fontSize: "13px",
            }}
          >
            <CompleteIcon sx={{ fontSize: "18px" }} />
            {translate("execution.markCompleteTitle")}
          </Box>
        )}

        {/* Work Completed: waiting indicator (provider) */}
        {isWorkCompletedProvider && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "rgba(34,197,94,0.08)",
              color: "#16A34A",
              borderRadius: dt.radiusSm,
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              border: "1px solid rgba(34,197,94,0.2)",
            }}
          >
            <WaitingIcon sx={{ fontSize: "16px" }} />
            {translate("execution.waitingForConfirmation")}
          </Box>
        )}

        {/* Service Completed (provider) */}
        {isServiceCompletedProvider && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "rgba(5,150,105,0.08)",
              color: "#059669",
              borderRadius: dt.radiusSm,
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              border: "1px solid rgba(5,150,105,0.2)",
            }}
          >
            {translate("serviceOffers:offerDetail.serviceCompletedInfo")}
          </Box>
        )}

        {/* ═══════════════════════════════
             OWNER ACTIONS
           ═══════════════════════════════ */}

        {/* Sent/Viewed/Negotiating: Accept + Reject + Compare */}
        {canAccept && (
          <Button
            variant="contained"
            startIcon={<AcceptIcon />}
            onClick={handleAcceptOffer}
            disabled={actionLoading}
            sx={{
              ...btnBase,
              fontWeight: 700,
              fontSize: "14px",
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#059669" },
            }}
          >
            {translate("servicing:acceptOffer")}
          </Button>
        )}
        {canReject && (
          <Button
            variant="outlined"
            startIcon={<RejectIcon />}
            onClick={() => setRejectDialogOpen(true)}
            disabled={actionLoading}
            sx={{
              ...btnBase,
              borderColor: "#EF4444",
              color: "#EF4444",
              "&:hover": { borderColor: "#DC2626", color: "#DC2626", backgroundColor: "rgba(239,68,68,0.04)" },
            }}
          >
            {translate("servicing:rejectOffer")}
          </Button>
        )}
        {(canAccept || canReject) && serviceRequestId && (
          <Button
            startIcon={<CompareIcon />}
            onClick={handleCompareOffers}
            sx={{
              ...btnBase,
              color: dt.textSecondary,
              "&:hover": { backgroundColor: dt.blueSoft, color: dt.blue },
            }}
          >
            {translate("servicing:compareOffers")}
          </Button>
        )}

        {/* Awaiting Confirmation: Cancel acceptance (owner) */}
        {canCancelAcceptance && (
          <Button
            variant="outlined"
            startIcon={<CancelIcon />}
            onClick={handleCancelAcceptance}
            disabled={actionLoading}
            sx={{
              ...btnBase,
              borderColor: "#F59E0B",
              color: "#F59E0B",
              "&:hover": { borderColor: "#D97706", color: "#D97706", backgroundColor: "rgba(245,158,11,0.04)" },
            }}
          >
            {translate("servicing:offerActions.cancelAcceptance")}
          </Button>
        )}

        {/* Owner: waiting for provider */}
        {isWaitingForProvider && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "rgba(245,158,11,0.08)",
              color: "#D97706",
              borderRadius: dt.radiusSm,
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              border: "1px solid rgba(245,158,11,0.2)",
            }}
          >
            <WaitingIcon sx={{ fontSize: "16px" }} />
            {translate("serviceOffers:offerDetail.waitingForProvider")}
          </Box>
        )}

        {/* Owner: work in progress */}
        {isOwnerWorkInProgress && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "rgba(59,130,246,0.08)",
              color: "#3B82F6",
              borderRadius: dt.radiusSm,
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              border: "1px solid rgba(59,130,246,0.2)",
            }}
          >
            <PlayIcon sx={{ fontSize: "16px" }} />
            {translate("serviceOffers:offerDetail.ownerWorkInProgress")}
          </Box>
        )}

        {/* Owner: work completed — confirm or request revision */}
        {isOwnerWorkCompleted && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "rgba(34,197,94,0.08)",
              color: "#16A34A",
              borderRadius: dt.radiusSm,
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              border: "1px solid rgba(34,197,94,0.2)",
            }}
          >
            <CompleteIcon sx={{ fontSize: "16px" }} />
            {translate("serviceOffers:offerDetail.reviewCompletion")}
          </Box>
        )}

        {/* Owner: service completed */}
        {isOwnerServiceCompleted && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              backgroundColor: "rgba(5,150,105,0.08)",
              color: "#059669",
              borderRadius: dt.radiusSm,
              padding: "8px 14px",
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              border: "1px solid rgba(5,150,105,0.2)",
            }}
          >
            {translate("serviceOffers:offerDetail.serviceCompletedInfo")}
          </Box>
        )}

        {/* Terminal states */}
        {isTerminal && (
          <Box
            sx={{
              color: dt.textMuted,
              fontFamily: dt.font,
              fontSize: "13px",
              fontStyle: "italic",
            }}
          >
            {translate("detailDialog.noActions")}
          </Box>
        )}
      </Box>

      {/* Reject reason dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontFamily: dt.font, fontWeight: 700 }}>
          {translate("servicing:rejectOfferTitle")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ fontFamily: dt.font, fontSize: "14px", color: dt.textSecondary, mb: 2 }}>
            {translate("servicing:rejectOfferText")}
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder={translate("servicing:rejectionReasonPlaceholder")}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{
              "& .MuiInputBase-root": { fontFamily: dt.font, fontSize: "14px" },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setRejectDialogOpen(false)}
            sx={{ ...btnBase, color: dt.textSecondary }}
          >
            {translate("common:common.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleRejectSubmit}
            disabled={actionLoading}
            sx={{
              ...btnBase,
              backgroundColor: "#EF4444",
              "&:hover": { backgroundColor: "#DC2626" },
            }}
          >
            {translate("servicing:rejectOffer")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OfferActionBar;
